import AppInput from '@nxsys/components/AppInput';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useDebounce } from '@nxsys/hooks/useDebounce';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { exportActions } from '@nxsys/store/slices/export/reducer/exportSlice';
import {
  ExportCompanyParams,
  GetCompanyToExportParams,
} from '@nxsys/store/slices/export/types';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ExportFilterModal from './ExportFilterModal';
import { resolveFilterCompanyIds } from '@nxsys/utils/companyScope';

type ExportCompanyModalProps = {
  isOpen: boolean;
  toggle: () => void;
  onExport: (values: any) => void;
};

const ExportCompanyModal: React.FC<ExportCompanyModalProps> = ({
  isOpen,
  toggle,
  onExport,
}: ExportCompanyModalProps) => {
  const { messages } = useTypedIntl();

  const dispatch = useDispatch<AppDispatch>();
  const { isBureau, isBureauEmployer, user, isAgency } = useSelector(
    (state: AppState) => state.appStore,
  );
  const { companyList } = useSelector((state: AppState) => state.exportStore);
  const { loading, list, pagination, queryParams } = companyList;

  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [isDirtySearch, setIsDirtySearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearch = useDebounce(searchValue, 500);
  const [exportFilterModal, setExportFilterModal] = useState({
    open: false,
  });

  useEffect(() => {
    if (isOpen)
      handleFetchData({
        page: 0,
        size: 20,
        sortField: 'createdAt',
        sortType: 'desc',
        // Umbrella exports more payment models than PAYEE, so count every
        // worker there, as the Companies page does for an admin.
        paymentModalEmployee: isBureau ? 'PAYEE' : undefined,
      });
  }, [isOpen]);

  useEffect(() => {
    if (isDirtySearch)
      handleFetchData({
        ...queryParams,
        page: 0,
        search: debouncedSearch,
      });
  }, [debouncedSearch]);

  const handleFetchData = (params: GetCompanyToExportParams) => {
    params.companyIds = resolveFilterCompanyIds(
      params.companyIds,
      isBureauEmployer,
      user?.companyUserIds,
    );
    dispatch(exportActions.getCompanyToExport(params));
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const columns: ColumnsType<any> = [
    {
      key: 'id',
      title: messages['export.company.companyId'],
      dataIndex: 'id',
    },
    {
      key: 'companyName',
      title: messages['export.company.companyName'],
      dataIndex: 'companyName',
    },
    {
      key: 'status',
      title: messages['export.company.status'],
      dataIndex: 'isActive',
      render: (record) => (record ? 'Active' : 'Inactive'),
    },
    {
      key: 'totalEmployees',
      title: messages['export.company.totalEmployees'],
      dataIndex: 'totalEmployees',
    },
    {
      key: 'payFrequency',
      title: messages['export.company.payFrequency'],
      dataIndex: 'payFrequency',
    },
    {
      key: 'lastPeriod',
      title: messages['export.company.lastPeriod'],
      render: (record) => {
        const latestWeeklyPayroll = record?.latestWeeklyPayroll?.updatedAt
          ? moment(record?.latestWeeklyPayroll?.updatedAt).format('DD/MM/YYYY')
          : '';
        const latest2WeeklyPayroll = record?.latest2WeeklyPayroll?.updatedAt
          ? moment(record?.latest2WeeklyPayroll?.updatedAt).format('DD/MM/YYYY')
          : '';
        const latest4WeeklyPayroll = record?.latest4WeeklyPayroll?.updatedAt
          ? moment(record?.latest4WeeklyPayroll?.updatedAt).format('DD/MM/YYYY')
          : '';
        const latestMonthlyPayroll = record?.latestMonthlyPayroll?.updatedAt
          ? moment(record?.latestMonthlyPayroll?.updatedAt).format('DD/MM/YYYY')
          : '';
        return (
          <div>
            {latestWeeklyPayroll && (
              <div className="w-100">Weekly: {latestWeeklyPayroll}</div>
            )}
            {latest2WeeklyPayroll && (
              <div className="w-100">2 Weekly: {latest2WeeklyPayroll}</div>
            )}
            {latest4WeeklyPayroll && (
              <div className="w-100">4 Weekly: {latest4WeeklyPayroll}</div>
            )}
            {latestMonthlyPayroll && (
              <div className="w-100">Monthly: {latestMonthlyPayroll}</div>
            )}
          </div>
        );
      },
      width: 220,
    },
  ];

  const toggleExportFilter = () =>
    setExportFilterModal({
      ...exportFilterModal,
      open: !exportFilterModal.open,
    });

  const handleExport = async (values?: Omit<ExportCompanyParams, 'ids'>) => {
    onExport({
      ids: selectedRows.map((row) => row.id),
      ...values,
    });
    toggle();

    if (!isAgency) {
      toggleExportFilter();
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    setIsDirtySearch(true);
  };

  return (
    <AppModalContainer
      title={messages['export.company.title']}
      isOpen={isOpen}
      toggle={toggle}
      width={1200}
      className="max-sm:w-[100vw]"
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.next'],
          buttonType: 'primary',
          disabled: loading || selectedRows.length === 0,
          onClick: () => (isAgency ? handleExport() : toggleExportFilter()),
        },
      ]}
      noPaddingBody
      minHeight="0px"
    >
      <AppsHeader>
        <div>
          <AppInput
            value={searchValue}
            onChange={handleSearchChange}
            placeholder="Search ..."
          />
        </div>
      </AppsHeader>
      <AppsContent>
        <AppTableContainer
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 1000, y: 700 }}
          titleEmpty={messages['export.company.empty']}
          columns={columns}
          onChangePagination={onChangePagination}
          rowSelection={{
            type: 'checkbox',
            onChange: (_: React.Key[], selectedRows: any[]) => {
              setSelectedRows(selectedRows);
            },
          }}
        />
      </AppsContent>
      <ExportFilterModal
        isOpen={exportFilterModal.open}
        toggle={toggleExportFilter}
        onExport={handleExport}
      />
    </AppModalContainer>
  );
};

export default ExportCompanyModal;
